import { ReactElement, useContext, useEffect, useState } from "react";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import {
  IAddition,
  IDelayBase, IEAFProduction, IProduction,
  IProductionExtended,
  IStrandProduction,
  IVariableGroup
} from "../../../types";
import { Paper, useTheme } from "@mui/material";
import KeyValueItem from "../../../components/charts/keyValueItem";
import Detail from "../../../components/dialogs/detail";
import HeatInformation from "./heatInformation";
import {
  formatNumber,
  formatTimeWithSeconds, getCurrentDatetimeInSeconds, toTons
} from "../../../system/utils";
import { Alert } from "@mui/material";
import {
  xProductionDurationAccessorScaled,
} from "../utils";
import { SystemContext } from "../../../context/systemContext";
import { getLanguage } from "../configuration/language";
import { getPalette } from "../../../configuration/configuration";
import SkeletonGantt from "./skeletonGantt";
import { Duration } from "luxon";
import ProductionInformation from "../../mill/components/productionInformation";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";
import HtmlTooltip from "../../../components/charts/tooltip";
import AdditionsLayerGantt from "./additionsLayerGantt";
import VariablesLayerGantt from "../variablesLayerGantt";
import SamplesLayerGantt from "./samplesLayerGantt";

interface IHeatChartProps {
  title: string;
  productionData: IProduction[];
  parentSize: {
    width: number;
    height: number;
  } | null;
}

const heatHeight = 50;
// const sliderHeight =  30;
const marginTop = 40;
// const shiftHeader = 30;
const marginBottom = 60;


const GanttChartMobile = ({ productionData, title, parentSize }: IHeatChartProps): ReactElement => {
  const theme = useTheme();
  const systemContext = useContext(SystemContext);
  const [openDetail, setOpenDetail] = useState(false);
  const [maximum, setMaximum] = useState(0);
  const [selectedProduction, setProductionItem] = useState<IProductionExtended | null>(null);
  const chartID = useUniqueId("gantt-chart-mobile");
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: productionData.length * heatHeight + marginTop + marginBottom,
    marginBottom,
    marginLeft: 40,
    marginRight: 40,
    marginTop
  });


  useEffect(() => {
    const heats = productionData.map(item => item.endDatetime ? item.endDatetime - item.beginDatetime : getCurrentDatetimeInSeconds() - item.beginDatetime);
    setMaximum(Math.max(...heats));
  }, [productionData]);


  if (productionData.length === 0) {
    return (
      <Paper>
        <Alert severity="warning">No Data to Display.</Alert>
      </Paper>
    );
  }

  if (maximum === 0) {
    return <SkeletonGantt/>;
  }

  const xScale = d3.scaleLinear()
    .domain([0, maximum])
    .range([0, dimensions.boundedWidth]);

  const onSelected = (selectedItem: IProductionExtended) => {
    setProductionItem(selectedItem);
    setOpenDetail(true);
  };

  const isHeat = productionData[0].productionMachines[0].__typename === "EAFProduction";

  return (
    <Paper ref={ref} style={{ height: dimensions.height }} elevation={0}>
      <Chart dimensions={dimensions}>
        <defs>
          <clipPath id="cut-off">
            <rect x="0" y="0" width={dimensions.boundedWidth} height={dimensions.boundedHeight} />
          </clipPath>
        </defs>

        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label={title}
          ticks={getTicks(xScale.domain(), parentSize && parentSize.width > 600 ? 5 : 3)}
          formatTick={(value) => Duration.fromMillis(value as number * 1000).toFormat("hh:mm:ss")}
        />

        <g transform={`translate(${0}, ${-20})`}>
          <g transform={`translate(${0}, ${-10})`}>
            <polygon fill={"green"} points="5 0 0 5 5 10 10 5 5 0"/>
          </g>
          <text y={0}
            x={12}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).samples}
          </text>

          <g transform={`translate(${78}, ${-10})`}>
            <polygon fill={"#d84315"} points="0 0 5 10 10 0 0 0"/>
          </g>
          <text y={0}
            x={90}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).variables}
          </text>

          {isHeat &&
          <>
            <circle fill={"purple"} cx={163} cy={-5} r={5}/>
            <text y={0}
              x={170}
              fill={theme.palette.text.primary}
              fontSize="1em"
              fillOpacity="0.9"
              textAnchor="start">
              {getLanguage(systemContext.languageCode).buckets}
            </text>

            <rect fill={"blue"} x={238} y={-9} width={8} height={8}/>
            <text y={0}
              x={250}
              fill={theme.palette.text.primary}
              fontSize="1em"
              fillOpacity="0.9"
              textAnchor="start">
              {getLanguage(systemContext.languageCode).alloys}
            </text>
          </>
          }
        </g>

        <g clipPath="url(#cut-off)">
          {productionData.map((production: IProduction, productionIndex: number) => {
            const heatGroup = productionIndex * heatHeight + 27;
            const productionKey = `production-${production.productionID}`;
            const showMachineName = production.productionMachines.length > 1;

            return (
              <g key={productionKey}>
                <line className="Axis__line" strokeWidth="0.5" x1={0} x2={dimensions.boundedWidth} y1={heatGroup - 25} y2={heatGroup - 25} />

                {production.productionMachines.map((productionItem: IProductionExtended, heatIndex: number) => {
                  const heatKey = `${chartID}-heat-${productionItem.title}-${heatIndex}`;
                  const heatY = 0;
                  const heatWidth = xProductionDurationAccessorScaled(productionItem, xScale);

                  const variables: IVariableGroup[] = [];
                  productionItem.variables.forEach(variable => {
                    const item = variables.find(x => x.datetime === variable.datetime);
                    if (!item) {
                      variables.push({
                        datetime: variable.datetime,
                        variables: [ variable ]
                      });
                    } else {
                      item.variables.push(variable);
                    }
                  });

                  const heatData = (productionItem as any).__typename === "EAFProduction" ? productionItem as IEAFProduction : null;
                  const rmData = (productionItem as any).__typename === "StrandProduction" ? productionItem as IStrandProduction : null;

                  const flag = heatWidth / (showMachineName ? productionItem.machine.name : productionItem.reference ?? productionItem.title).length > 8.3;

                  const xPos = xScale(productionItem.beginDatetime - production.beginDatetime);

                  const xAccessorScaled = (d: IDelayBase) => xScale(d.beginDatetime - productionItem.beginDatetime);
                  const widthAccessorScaled = (d: IDelayBase) => xScale((d.endDatetime ?? productionItem.endDatetime ?? getCurrentDatetimeInSeconds()) - d.beginDatetime);

                  return(
                    <g key={heatKey} transform={`translate(${xPos}, ${heatGroup})`}  height={heatHeight} onClick={() => onSelected(productionItem)}>
                      {showMachineName && heatIndex === 0 && <text
                        y={-15}
                        x={0}
                        fontSize="1em"
                        textAnchor="start"
                        fill={"#444"}
                        fontWeight={"bold"}>
                        {productionItem.reference ?? productionItem.title}
                      </text>}

                      <HtmlTooltip title={
                        <div>
                          <div className="display-flex-column align-items-center" >
                            <strong>Heat</strong>
                            <strong>{productionItem.title}</strong>
                          </div>
                          <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(productionItem.beginDatetime as number)} />
                          <KeyValueItem name={"End"} value={formatTimeWithSeconds(productionItem.endDatetime as number)} />
                          {heatData &&
                          <>
                            <KeyValueItem name={"Steel"} value={heatData.steel.code}/>
                            <KeyValueItem name={"Weight"} value={formatNumber(toTons(heatData.weight), 2)} unit="US ton" />
                            <KeyValueItem name={"Power ON"} value={formatNumber(heatData.powerON, 2)} unit="min" />
                            <KeyValueItem name={"Power OFF"} value={formatNumber(heatData.powerOFF, 2)} unit="min" />
                            <KeyValueItem name={"Tap to Tap"} value={formatNumber(heatData.tapToTap / 60, 2)} unit="min" />
                          </>
                          }
                          { rmData &&
                          <>
                            <KeyValueItem name={"Heat Number"} value={rmData.heatNumber}/>
                            <KeyValueItem name={"Steel"} value={rmData.steelCode}/>
                          </>
                          }
                        </div>
                      }>
                        <g>
                          <rect
                            cursor="pointer"
                            fill={getPalette().statusColor.production.background}
                            x={0}
                            y={heatY}
                            width={heatWidth}
                            height={20}
                          />
                        </g>
                      </HtmlTooltip>

                      <DelayLayerGantt delays={productionItem.delays} height={20} y={heatY} xAccessorScaled={xAccessorScaled}
                        widthAccessorScaled={widthAccessorScaled} selected={null} chartKey={chartID} />

                      <SamplesLayerGantt chartID={heatKey} title={productionItem.title}
                        samples={productionItem.samples}
                        y={heatY - 13} xAccessorScaled={sample => xScale((sample.sampleDatetime ?? productionItem.beginDatetime) - productionItem.beginDatetime) - 4} />

                      <VariablesLayerGantt chartID={heatKey} title={productionItem.title} variableGroups={variables} y={heatY - 13} xAccessorScaled={variableGroup => xScale(variableGroup.datetime) - 5} />

                      {heatData &&
                      <AdditionsLayerGantt chartID={heatKey} title={productionItem.title}
                        additions={heatData.additions.filter(x => x.beginDatetime)}
                        xAccessorScaled={(addition: IAddition) => xScale((addition.beginDatetime ?? productionItem.beginDatetime) - productionItem.beginDatetime) - 4}
                        y={heatY - 12}/>}

                      <text
                        y={heatY + 15}
                        x={heatWidth / 2.0}
                        fontSize="1em"
                        textAnchor="middle"
                        fill={flag ? "white" : "#444"}
                        fontWeight={"bold"}
                        onClick={() => onSelected(productionItem)}>
                        {showMachineName ? productionItem.machine.name : productionItem.reference ?? productionItem.title}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })
          }
        </g>
      </Chart>
      {openDetail && selectedProduction &&
      <Detail closed={() => setOpenDetail(false)} fullWidth={true} maxWidth={"xl"} title="Detail">
        <>
          {(selectedProduction as any).__typename === "EAFProduction" && <HeatInformation productionItem={selectedProduction} height={500}/>}
          {(selectedProduction as any).__typename === "StrandProduction" && <ProductionInformation productionItem={selectedProduction as IStrandProduction} height={500}/>}
        </>
      </Detail>
      }
    </Paper>
  );
};

export default GanttChartMobile;
